import { Fragment, useMemo, useState } from 'react';
import type { SalesResearchRun, SalesResearchStatus } from '../../../api/salesResearch';
import { EmptyState } from '../../../components/ui/States';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { Badge } from '../../../components/ui/Badge';
import { Nil, Table, TableFooter, TGroupRow, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { formatDayMonthYear, formatTime } from '../../../utils/dateFormat';
import { formatCostUsd } from './ResearchStatusPill';
import { isRunActive } from '../../../api/salesResearch';

const URL_IN_NAME = /\s*\(?\s*(https?:\/\/[^\s)]+)\s*\)?\s*/i;

/** "example.com" from "https://www.example.com/about". */
function domainOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * A company typed as "Weishardt (https://www.weishardt.com/)" shows as
 * "Weishardt" with the URL's domain underneath, never the raw URL.
 */
export function companyDisplay(run: Pick<SalesResearchRun, 'companyName' | 'companyDomain'>): { name: string; domain: string | null } {
  const match = URL_IN_NAME.exec(run.companyName);
  if (!match) return { name: run.companyName, domain: run.companyDomain };
  const name = run.companyName.replace(URL_IN_NAME, ' ').trim() || domainOf(match[1]!) || run.companyName;
  return { name, domain: domainOf(match[1]!) ?? run.companyDomain };
}

const DOMAIN_SHAPED = /^(https?:\/\/)?(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}\/?$/i;

/** True for a name that is just a web address ("weishardt.com"), typed in place of a company name. */
function isDomainName(name: string): boolean {
  return DOMAIN_SHAPED.test(name.trim());
}

/** A run with how many briefs the company has in all. */
export type CompanyBrief<T> = T & { briefCount: number };

/**
 * Pure: one row per company, not one per run. The company is its domain (or its name without one); the row is
 * the run in flight, else the latest ready one, else the latest. Its name is a real company name where any run
 * of the company has one, never the bare domain. Newest first, as given. Exported for tests.
 */
export function latestPerCompany<T extends Pick<SalesResearchRun, 'companyName' | 'companyDomain' | 'status' | 'createdAt'>>(
  runs: readonly T[],
): Array<CompanyBrief<T>> {
  const rank = (r: T) => (isRunActive(r.status) ? 2 : r.status === 'ready' ? 1 : 0);
  const groups = new Map<string, T[]>();
  for (const run of runs) {
    const d = companyDisplay(run);
    const key = (d.domain ?? '').toLowerCase().replace(/^www\./, '') || d.name.trim().toLowerCase();
    groups.set(key, [...(groups.get(key) ?? []), run]);
  }
  const out: Array<CompanyBrief<T>> = [];
  for (const group of groups.values()) {
    const pick = group.reduce((best, r) => (rank(r) > rank(best) || (rank(r) === rank(best) && r.createdAt > best.createdAt) ? r : best));
    const named = group.find((r) => !isDomainName(companyDisplay(r).name));
    out.push({ ...pick, companyName: named && isDomainName(companyDisplay(pick).name) ? companyDisplay(named).name : pick.companyName, briefCount: group.length });
  }
  return out.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

/**
 * Ready is the normal outcome, so it shows nothing. Only exceptions get a pill:
 * failed (red) and cancelled (neutral). In-flight runs read as quiet text.
 */
const STATUS_VARIANT: Partial<Record<SalesResearchStatus, 'error' | 'default'>> = {
  failed: 'error',
  cancelled: 'default',
};

function RunStatus({ status }: { status: SalesResearchStatus }) {
  if (status === 'ready') return null;
  const variant = STATUS_VARIANT[status];
  if (variant) return <Badge variant={variant}>{humanStatus(status)}</Badge>;
  return <span className="text-body-xs text-text-tertiary">{humanStatus(status)}…</span>;
}

/**
 * Every brief ever run is in this list, so it is filtered rather than scrolled.
 * "Ready" is its own filter and not the default: a failed run is exactly what
 * someone comes looking for when they wonder why a company has no brief.
 */
type Filter = 'all' | 'ready' | 'unfinished';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'ready', label: 'Ready' },
  { id: 'unfinished', label: 'Failed or cancelled' },
] as const satisfies readonly { id: Filter; label: string }[];

interface Props {
  runs: SalesResearchRun[];
  selectedId: string | null;
  onSelect: (runId: string) => void;
  /** True when the history is long enough to have hit the server's cap. */
  truncated?: boolean;
}

export function ResearchRunList({ runs, selectedId, onSelect, truncated }: Props) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const companies = useMemo(() => latestPerCompany(runs), [runs]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return companies.filter((run) => {
      if (filter === 'ready' && run.status !== 'ready') return false;
      if (filter === 'unfinished' && !isUnfinished(run.status)) return false;
      if (!needle) return true;
      // Leads are searched too — half the time the brief is remembered by the
      // person it was about rather than by the company.
      return [run.companyName, run.companyDomain ?? '', ...run.leads.map((l) => l.name)]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [companies, query, filter]);

  const { pageItems, ...paging } = usePagination(visible);
  // The page's rows grouped by the day they were run, newest day first, so a
  // day with several briefs says its date once instead of on every row.
  const days = useMemo(() => groupByDay(pageItems), [pageItems]);
  // A column that is empty on every row says nothing; it comes back once a brief names a lead.
  const showLeads = useMemo(() => runs.some((run) => run.leads.length > 0), [runs]);
  // Spend across the filtered list, not just this page: the footer answers "what did these cost".
  const totalCost = useMemo(() => visible.reduce((sum, run) => sum + (Number(run.costUsd) || 0), 0), [visible]);

  if (runs.length === 0) {
    return <EmptyState title="No research yet" description="Run one above." compact />;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search company, domain or lead"
          className="min-w-48 flex-1 rounded border border-border-light bg-surface-primary px-3 py-1.5 text-body-sm"
        />
        <SegmentedControl options={FILTERS} value={filter} onChange={setFilter} ariaLabel="Status" />
        <span className="text-body-xs text-text-tertiary">
          {visible.length} of {companies.length} companies
        </span>
      </div>

      {visible.length === 0 ? (
        <EmptyState title="No brief matches that" compact />
      ) : (
        <>
          <Table minWidth={720}>
            <THead>
              <tr>
                <Th>Company</Th>
                {showLeads && <Th>Leads</Th>}
                {/* Cost is on the list on purpose: a run that cost far more than
                    the others should be visible here, not on a bill. */}
                <Th align="right" title="Billed in US dollars; no EUR rate is available here">Cost (USD)</Th>
                <Th align="right">Time</Th>
              </tr>
            </THead>
            <tbody>
              {days.map((day) => (
                <Fragment key={day.key}>
                  <TGroupRow
                    colSpan={showLeads ? 4 : 3}
                    label={day.label}
                    summary={day.runs.length === 1 ? '1 company' : `${day.runs.length} companies`}
                  />
              {day.runs.map((run) => {
                const company = companyDisplay(run);
                return (
                  <Tr
                    key={run.id}
                    onClick={() => onSelect(run.id)}
                    className={selectedId === run.id ? 'bg-surface-secondary' : undefined}
                  >
                    <Td>
                      <span className="font-medium text-text-primary">{company.name}</span>
                      {/* Ready is the norm; only the exceptions get an inline tag. */}
                      {run.status !== 'ready' && (
                        <span className="ml-2 align-middle">
                          <RunStatus status={run.status} />
                        </span>
                      )}
                      {(company.domain || run.briefCount > 1) && (
                        <span className="block text-body-xs text-text-tertiary">
                          {[company.domain, run.briefCount > 1 ? `latest of ${run.briefCount} briefs` : null].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </Td>
                    {showLeads && (
                      <Td className="text-text-secondary">
                        {run.leads.length > 0 ? run.leads.map((l) => l.name).join(', ') : <Nil />}
                      </Td>
                    )}
                    <Td align="right" className="text-text-tertiary tabular-nums whitespace-nowrap">
                      {run.costUsd ? formatCostUsd(run.costUsd) : <Nil />}
                    </Td>
                    <Td align="right" className="text-text-tertiary whitespace-nowrap">{formatTime(run.createdAt)}</Td>
                  </Tr>
                );
              })}
                </Fragment>
              ))}
            </tbody>
          </Table>
          {/* The header already gives the totals for everything; the footer only says what a filter shows. */}
          {visible.length !== companies.length && (
            <TableFooter>
              {visible.length === 1 ? '1 company' : `${visible.length} companies`} shown · {formatCostUsd(totalCost)} on their latest briefs (USD)
            </TableFooter>
          )}
          <Pagination {...paging} onPage={paging.setPage} />
        </>
      )}

      {truncated && (
        <p className="text-body-xs text-text-tertiary">
          Only the most recent briefs are listed. Older ones are still stored but are not shown
          here yet.
        </p>
      )}
    </div>
  );
}

/** "in_progress" → "In progress". */
function humanStatus(status: string): string {
  const text = status.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function isUnfinished(status: SalesResearchStatus): boolean {
  return status === 'failed' || status === 'cancelled';
}

/** Local calendar day, YYYY-MM-DD, for grouping. */
function localDayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "Today", "Yesterday", else "4 Sep 2026". */
function dayLabel(iso: string): string {
  const key = localDayKey(iso);
  if (key === localDayKey(new Date().toISOString())) return 'Today';
  if (key === localDayKey(new Date(Date.now() - 86_400_000).toISOString())) return 'Yesterday';
  return formatDayMonthYear(new Date(iso));
}

/** Pure: consecutive runs (already newest first) bucketed by local day. Exported for unit tests. */
export function groupByDay<T extends { createdAt: string }>(runs: T[]): Array<{ key: string; label: string; runs: T[] }> {
  const out: Array<{ key: string; label: string; runs: T[] }> = [];
  for (const run of runs) {
    const key = localDayKey(run.createdAt);
    const last = out[out.length - 1];
    if (last && last.key === key) last.runs.push(run);
    else out.push({ key, label: dayLabel(run.createdAt), runs: [run] });
  }
  return out;
}
